import React, { useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { BsCalendarDate } from "react-icons/bs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../../components/calendartheme.css";
import AddNetworkModal from "../../components/AffiliatePartnerPortal/AddNetworkModal";
import { networkMemberAPI } from "../../services/AffiliatePartnerPortal/networklistAPI";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/AuthContext";

const NetworkList = ({ Id }) => {
  const datePickerRef = useRef(null);
  const { user } = useAuth();

  // ✅ State
  const [customers, setCustomers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Search + Date Range states
  const [searchTerm, setSearchTerm] = useState("");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // ✅ Fetch members by user ID
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        if (!Id) return;
        setLoading(true);

        const response = await networkMemberAPI.getByUser(Id);
        setCustomers(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Failed to fetch customers.");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [Id]);

  // ✅ Handle search
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // ✅ Handle date range selection
  const setDateRange = (range) => {
    if (range && range.length === 2) {
      setStartDate(range[0]);
      setEndDate(range[1]);
    }
  };

  // ✅ Select all toggle
  const handleSelectAll = () => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    if (newSelectedAll) {
      setSelectedCustomers(customers.map((c) => c?._id).filter(Boolean));
    } else {
      setSelectedCustomers([]);
    }
  };

  // ✅ Handle individual selection
  const handleSelectCustomer = (customerId) => {
    setSelectedCustomers((prevSelected) =>
      prevSelected.includes(customerId)
        ? prevSelected.filter((id) => id !== customerId)
        : [...prevSelected, customerId]
    );
  };

  // ✅ Save new member (just update state, no duplicate API call)
  const handleSaveMember = (newMember) => {
    toast.success("Network member added successfully 🎉");
    setIsModalOpen(false);
    setCustomers((prev) => [newMember, ...prev]);
  };

  // ✅ Export to Excel + ZIP
  const exportToExcelAndZip = async () => {
    const customersToExport =
      selectedCustomers.length > 0
        ? customers.filter((c) => c && selectedCustomers.includes(c._id))
        : customers;

    if (customersToExport.length === 0) {
      toast.warn("No customers to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const dataForExcel = customersToExport.map((c) => ({
      "Full Name": c?.fullName || "N/A",
      "Mobile Number": c?.mobile || "N/A",
      "Work Type": c?.workType || "N/A",
      Work: c?.work || "N/A",
      Age: c?.age || "N/A",
      Address: c?.address || "N/A",
      "Relation Type": c?.relationType || "N/A",
      "Created At": c?.createdAt
        ? new Date(c.createdAt).toLocaleDateString()
        : "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "NetworkMembers");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("network_members.xlsx", excelBuffer);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "network_members.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ✅ Apply filters (safe checks added)
  const filteredCustomers = customers.filter((c) => {
    if (!c) return false;

    const matchesSearch =
      c?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c?.mobile?.toLowerCase().includes(searchTerm.toLowerCase());

    const createdAt = c?.createdAt ? new Date(c.createdAt) : null;
    const matchesDate =
      (!startDate || (createdAt && createdAt >= startDate)) &&
      (!endDate || (createdAt && createdAt <= endDate));

    return matchesSearch && matchesDate;
  });

  return (
    <div className="text-white">
      {/* Header */}
      <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 mb-4 ml-4">
        {/* Search */}
        <div className="relative flex items-center bg-transparent border border-[#333333] rounded-full w-full sm:w-80">
          <FiSearch className="absolute left-4 w-[24px] text-[#F3F3F3]" />
          <input
            type="text"
            placeholder="Search by Name or Mobile"
            className="bg-transparent text-white rounded-md pl-10 py-2.5 leading-normal focus:outline-none w-64"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* Date Picker */}
        <div
          className="relative border border-[#333333] rounded-md"
          ref={datePickerRef}
        >
          <button
            className="flex items-center bg-transparent rounded-md px-2 sm:px-3 py-2 focus:outline-none text-sm sm:text-base"
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          >
            <BsCalendarDate className="mr-1 sm:mr-2 text-[#FFCA20] text-sm sm:text-base" />
            <span className="hidden sm:inline">
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : "Choose Dates"}
            </span>
          </button>

          {isDatePickerOpen && (
            <div className="absolute z-10 mt-2 left-0 bg-[#1e1e1e] rounded-lg p-2 border border-[#333333]">
              <Calendar
                selectRange={true}
                onChange={(update) => {
                  if (update[0] && update[1]) {
                    setDateRange(update);
                    setIsDatePickerOpen(false);
                  }
                }}
                value={[startDate, endDate]}
                calendarType="gregory"
                prev2Label={null}
                next2Label={null}
              />
            </div>
          )}
        </div>

        {user?.role !== "admin" && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center bg-[#FFCA20] text-black rounded-md px-4 py-2 font-semibold focus:outline-none"
          >
            Add Network
          </button>
        )}

        {/* Download Button */}
        <button
          onClick={exportToExcelAndZip}
          className="px-4 py-2 rounded-md bg-yellow-400 text-black font-bold"
        >
          Download as ZIP
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-white text-center py-10">Loading...</div>
      ) : (
        <div className="bg-[#131313] rounded-lg border border-[#333333] overflow-x-auto ml-4">
          <table className="min-w-full border-collapse border border-[#333333] rounded-lg overflow-hidden">
            <thead className="bg-[#1e1e1e] border-t border-[#333333] rounded-lg">
              <tr>
                <th className="border border-[#333333] px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5 cursor-pointer"
                    checked={selectedAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Full Name
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Mobile Number (WhatsApp)
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Work Type
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Work
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Age
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Address
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Relation Type
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#1e1e1e] border border-[#333333]">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-4 py-4 text-center text-[#F3F3F3]"
                  >
                    No customers found
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer?._id || Math.random()}>
                    <td className="border border-[#333333] px-4 py-3">
                      <input
                        type="checkbox"
                        className="h-5 w-5 cursor-pointer"
                        checked={selectedCustomers.includes(customer?._id)}
                        onChange={() =>
                          customer?._id && handleSelectCustomer(customer._id)
                        }
                      />
                    </td>
                    <td className="border border-[#333333] px-4 py-3 text-sm text-white">
                      {customer?.fullName || "N/A"}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.mobile || "N/A"}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.workType || "N/A"}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.work || "N/A"}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.age || "N/A"}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.address || "N/A"}
                    </td>
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.relationType || "N/A"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Network Modal */}
      <AddNetworkModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMember}
      />
    </div>
  );
};

export default NetworkList;
